import { MemoryData, MemoryMetric, MemoryRegion, MemoryScore } from "@/api/studio/insights/types/memory.type";
import { classColors } from "@/utils/classification-mapping";
import { MemoryEmotionIndicator } from "@/components/ui/indicator";

/* Prop Types */
interface MemoryRegionTableProps {
  regions: MemoryData["regions"];
  metric: MemoryMetric;
}

/*
 * MemoryRegionTable component
 *
 * Displays a table of regions for the selected memory metric (branding, visuals, or text).
 * Only shows regions that have a score for the selected metric.
 * Sorts regions by score from high to low (5 = Very strong, 1 = Very weak).
 */
export const MemoryRegionTable = ({ regions, metric }: MemoryRegionTableProps) => {
  /* Filter and sort regions */
  const getScoreForMetric = (region: MemoryRegion) => {
    if (metric === "visuals") return region.visual;
    if (metric === "branding") return region.branding;
    if (metric === "text") return region.text;
    return undefined;
  };

  // Filter regions that have a score for the selected metric
  const filteredRegions = regions.filter((region) => {
    const score = getScoreForMetric(region);
    return score !== undefined && score !== null;
  });

  // Sort by score (descending: 5 to 1, where 5 = Very strong, 1 = Very weak)
  const sortedRegions = filteredRegions.sort((a, b) => {
    const scoreA = getScoreForMetric(a) ?? 0;
    const scoreB = getScoreForMetric(b) ?? 0;
    return scoreB - scoreA;
  });

  // If no regions, show empty state
  if (sortedRegions.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center">
        <p className="text-sm text-base-content/60">No AOI elements found for {metric}</p>
      </div>
    );
  }

  /* Render */
  return (
    <div className="flex flex-col h-full mt-4">
      <div className="rounded-t-lg bg-base-300 px-4 py-3 items-center">
        <p className="text-xs opacity-60 font-bold">Element</p>
      </div>
      <div className="flex flex-col">
        {sortedRegions.map((region, i) => {
          const score = getScoreForMetric(region) ?? 0;

          return (
            <div
              key={i}
              className="grid grid-cols-[16px_4fr_1.5fr] gap-4 items-center py-3 justify-between border-b px-4 border-base-300 bg-base-100 last:border-b-0 last:rounded-b-lg"
            >
              <div className="size-4 rounded-xs" style={{ backgroundColor: classColors[region.classification] }} />
              <div className="flex items-center truncate">
                <span className="text-sm font-semibold truncate">{region.label}</span>
              </div>
              <div className="flex items-center">
                <MemoryEmotionIndicator score={score as MemoryScore} type="memory" size="small" />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
